/**
 * EM Portal — Assessment Requests page.
 * Engagement Managers review, approve, or reject client assessment requests.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Grid, Card, CardContent, Tabs, Tab, Tooltip,
} from '@mui/material';
import {
  CheckCircle, HourglassEmpty, Assignment,
  ThumbUp, ThumbDown, Business,
} from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

interface AssessmentRequestItem {
  id: string;
  organization_id: string;
  organization_name: string;
  organization_domain: string;
  requested_by: string;
  requester_name: string;
  requester_email: string;
  category: string;
  domain: string;
  business_justification: string;
  priority: string;
  status: string;
  review_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string | null;
}

const PRIORITY_COLORS: Record<string, 'default' | 'warning' | 'error' | 'info'> = {
  low: 'default', normal: 'info', high: 'warning', urgent: 'error',
};

const EMAssessmentRequests: React.FC = () => {
  const [requests, setRequests] = useState<AssessmentRequestItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabIdx, setTabIdx] = useState(0);

  // Review dialog
  const [reviewTarget, setReviewTarget] = useState<AssessmentRequestItem | null>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/sme/assessment-requests');
      const data = await res.json();
      if (data.success) setRequests(data.requests);
    } catch {
      setError('Failed to load assessment requests');
    } finally {
      setLastRefreshed(new Date());
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const resolvedRequests = requests.filter(r => r.status !== 'pending');

  const displayedRequests = tabIdx === 0 ? pendingRequests : resolvedRequests;

  const openReview = (req: AssessmentRequestItem, action: 'approve' | 'reject') => {
    setReviewTarget(req);
    setReviewAction(action);
    setReviewNotes('');
  };

  const handleReview = async () => {
    if (!reviewTarget) return;
    setSubmitting(true);
    try {
      const res = await authFetch(`/api/sme/assessment-requests/${reviewTarget.id}`, {
        method: 'PUT',
        body: JSON.stringify({ action: reviewAction, review_notes: reviewNotes }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(`Request ${reviewAction}d successfully`);
        setReviewTarget(null);
        fetchRequests();
      } else {
        setError(data.error || 'Review failed');
      }
    } catch {
      setError('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
          Assessment Requests
        </Typography>
        <RefreshButton onRefresh={fetchRequests} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review and manage assessment requests from client organizations.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: '4px solid #FF9800' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <HourglassEmpty color="warning" />
              <Typography variant="h3" color="warning.main">{pendingRequests.length}</Typography>
              <Typography variant="body2" color="text.secondary">Pending Review</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: '4px solid #4CAF50' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle color="success" />
              <Typography variant="h3" color="success.main">
                {requests.filter(r => r.status === 'approved').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Approved</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: '4px solid #00338D' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assignment color="primary" />
              <Typography variant="h3" color="primary">{requests.length}</Typography>
              <Typography variant="body2" color="text.secondary">Total Requests</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={tabIdx} onChange={(_, v) => setTabIdx(v)} sx={{ mb: 2 }}>
        <Tab label={`Pending (${pendingRequests.length})`} />
        <Tab label={`Resolved (${resolvedRequests.length})`} />
      </Tabs>

      {/* Requests Table */}
      {displayedRequests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {tabIdx === 0 ? 'No pending requests.' : 'No resolved requests yet.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Requester</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
                {tabIdx === 0 && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRequests.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.organization_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.organization_domain}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.requester_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.requester_email}</Typography>
                  </TableCell>
                  <TableCell><Chip label={r.category} size="small" variant="outlined" /></TableCell>
                  <TableCell>{r.domain}</TableCell>
                  <TableCell>
                    <Chip label={r.priority} size="small" color={PRIORITY_COLORS[r.priority] || 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip label={r.status} size="small" color={
                      r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'error' : 'warning'
                    } />
                  </TableCell>
                  <TableCell>
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                  {tabIdx === 0 && (
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Approve">
                          <Button
                            size="small" variant="contained" color="success"
                            startIcon={<ThumbUp />}
                            onClick={() => openReview(r, 'approve')}
                          >
                            Approve
                          </Button>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <Button
                            size="small" variant="outlined" color="error"
                            startIcon={<ThumbDown />}
                            onClick={() => openReview(r, 'reject')}
                          >
                            Reject
                          </Button>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Review Dialog */}
      <Dialog open={!!reviewTarget} onClose={() => setReviewTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reviewAction === 'approve' ? 'Approve' : 'Reject'} Assessment Request
        </DialogTitle>
        <DialogContent>
          {reviewTarget && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2"><strong>Organization:</strong> {reviewTarget.organization_name}</Typography>
              <Typography variant="body2"><strong>Requester:</strong> {reviewTarget.requester_name}</Typography>
              <Typography variant="body2"><strong>Domain:</strong> {reviewTarget.domain} ({reviewTarget.category})</Typography>
              {reviewTarget.business_justification && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Justification:</strong> {reviewTarget.business_justification}
                </Typography>
              )}
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Review Notes (optional)"
            placeholder={reviewAction === 'approve'
              ? "Any notes for the client about the approved assessment..."
              : "Reason for rejection..."
            }
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={reviewAction === 'approve' ? 'success' : 'error'}
            onClick={handleReview}
            disabled={submitting}
          >
            {submitting ? 'Processing...' : reviewAction === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EMAssessmentRequests;
